import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  Flex,
  Tooltip,
  Button,
  Checkbox,
  Text,
} from '@chakra-ui/react';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import SelectUserInput from 'components/common/forms/SelectUserInput';
import PropTypes from 'prop-types';
import { userBaseColumns, userRemoveColumn } from 'utils/columns/userColumns';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addClassUsersAction } from 'store/modules/class/class-action';
import { classActions } from 'store/modules/class/class-slice';
import { ReducerStatus } from 'utils/constants';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import SearchGroupMd from './SearchGroupMd';
import UserTable from '../UserTable';

function AddUsersMd({ isVisible, onClose, classId }) {
  const dispatch = useDispatch();

  const classItem = useSelector((state) => state.class.class);
  const addClassUsers = useSelector((state) => state.class.addClassUsers);

  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [isSendNotif, setIsSendNotif] = useState(false);

  const handleConfirmationModal = (modalValue) => {
    setConfirmationModal(modalValue);
  };

  const handleAddSubmit = () => {
    dispatch(
      addClassUsersAction({
        classId,
        emails: users.map((item) => item.value),
        isSendNotif,
      })
    );
  };

  const handleAddResult = () => {
    handleConfirmationModal(false);

    if (addClassUsers.status === ReducerStatus.SUCCEEDED) {
      dispatch(
        classActions.setAddClassUsers({
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
      setUsers([]);
      setIsSendNotif(false);
      onClose();
    } else {
      dispatch(classActions.setAddClassUsers({ status: ReducerStatus.IDLE }));
    }
  };

  const handleRemoveUser = (emailToRemove) => {
    setUsers((prevList) =>
      prevList.filter((item) => item.value !== emailToRemove)
    );
  };

  const handleRemoveSelectedUsers = () => {
    setUsers((prevUsers) =>
      prevUsers.filter(
        (user) =>
          !selectedUsers.some(
            (selectedUser) => selectedUser.value === user.value
          )
      )
    );
  };

  const handleAddGroupMembers = (members) => {
    setUsers((prevList) => [
      ...prevList,
      ...members.filter(
        (item2) => !prevList.some((item1) => item1.value === item2.value)
      ),
    ]);
  };

  return (
    <Box>
      <ConfirmationActionModal
        onClose={() => handleConfirmationModal(false)}
        onConfirm={handleAddSubmit}
        onSubmitResult={handleAddResult}
        isVisible={confirmationModal}
        status={addClassUsers.status}
        description="Are you sure you want to add these users to the class?"
        successMessage="User(s) added from class successfully"
        error={addClassUsers.error}
      />

      <Modal isOpen={isVisible} onClose={onClose} size="2xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Users</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack flex={1} gap={5} align="start">
              <FormikFormControl
                label="Select Users"
                helperText="Please select the users who will be participating in this class. You may leave this blank and add users after the class has been created."
              >
                <VStack gap={5} align="stretch">
                  <SelectUserInput
                    value={users}
                    isMulti
                    isClearable
                    onChangeHandler={setUsers}
                  />

                  <SearchGroupMd handleAdd={handleAddGroupMembers} />
                </VStack>
              </FormikFormControl>

              <Checkbox
                isChecked={isSendNotif}
                isDisabled={!classItem?.isPublished}
                onChange={(e) => setIsSendNotif(e.target.checked)}
              >
                Send Invitation Email&nbsp;
                {!classItem?.isPublished && (
                  <Text as="span" color="red">
                    (Class must be published to enable this feature)
                  </Text>
                )}
              </Checkbox>

              <UserTable
                users={users}
                columns={[
                  ...userBaseColumns,
                  userRemoveColumn(handleRemoveUser),
                ]}
                onSelectRowHandler={setSelectedUsers}
              >
                <Flex my={2} justify="end">
                  <Tooltip
                    label={
                      selectedUsers.length === 0 ? 'Select user first' : ''
                    }
                  >
                    <Button
                      isDisabled={selectedUsers.length === 0}
                      colorScheme="red"
                      size="xs"
                      onClick={handleRemoveSelectedUsers}
                    >
                      Remove
                    </Button>
                  </Tooltip>
                </Flex>
              </UserTable>
            </VStack>
          </ModalBody>
          <ModalFooter>
            {/* Close button */}
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
            {/* Proceed button */}
            <Button
              variant="outline"
              colorScheme="red"
              isLoading={addClassUsers.status === ReducerStatus.LOADING}
              onClick={() => handleConfirmationModal(true)}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

AddUsersMd.defaultProps = {
  classId: null,
};

AddUsersMd.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  classId: PropTypes.number,
};

export default AddUsersMd;
